/**
 * USR-02: Mantenedor de Roles y Permisos del Tenant
 * Permite crear, editar y eliminar roles con una matriz de permisos granular.
 */
import React, { useState, useMemo } from 'react'
import { Plus, Edit2, Trash2, Shield, ChevronDown, ChevronUp, ChevronRight, Loader2, X, Info, BadgeDollarSign, Search } from 'lucide-react'
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole } from '@/modules/users/hooks/useUsers'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { useToast } from '@/components/Toast'
import { Button } from '@/components/Button'
import { ROLE_NAMES } from '@/types'
import { POS_CAPABILITIES, POS_CAPABILITY_DEFAULTS } from '@/modules/pos/capabilities'
import type { Role, RoleType } from '@/types'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'

// ─── Permission Matrix Config ─────────────────────────────────────────────────

const MODULES: { key: string; label: string; group: string }[] = [
  { key: 'users', label: 'Usuarios', group: 'Sistema' },
  { key: 'config', label: 'Configuración', group: 'Sistema' },
  { key: 'sales.orders', label: 'Pedidos', group: 'Ventas' },
  // La venta de mostrador DESDE LA WEB (#1259). El POS externo es el grupo
  // «Punto de venta»: con el mismo rótulo, el administrador veía dos «punto de
  // venta» que habilitan cosas distintas.
  { key: 'sales.pos', label: 'Venta de mostrador (web)', group: 'Ventas' },
  { key: 'sales.documents', label: 'Documentos', group: 'Ventas' },
  { key: 'sales.customers', label: 'Clientes', group: 'Ventas' },
  { key: 'sales.products', label: 'Productos', group: 'Ventas' },
  { key: 'sales.collections', label: 'Cobranza', group: 'Ventas' },
  { key: 'sales.subscriptions', label: 'Suscripciones', group: 'Ventas' },
  { key: 'sales.contracts', label: 'Contratos', group: 'Ventas' },
  { key: 'sales.guides', label: 'Guías de Despacho', group: 'Ventas' },
  { key: 'sales.templates', label: 'Plantillas', group: 'Ventas' },
  { key: 'sales.commissions', label: 'Comisiones', group: 'Ventas' },
  { key: 'sales.forecast', label: 'Forecast', group: 'Ventas' },
  { key: 'purchases.suppliers', label: 'Proveedores', group: 'Compras' },
  { key: 'purchases.orders', label: 'Órdenes de Compra', group: 'Compras' },
  { key: 'purchases.invoices', label: 'Facturas Proveedor', group: 'Compras' },
  { key: 'purchases.payments', label: 'Pagos Proveedor', group: 'Compras' },
  { key: 'dte_inbound', label: 'DTEs Recibidos', group: 'Compras' },
  { key: 'accounting.accounts', label: 'Plan de Cuentas', group: 'Contabilidad' },
  { key: 'accounting.cost_centers', label: 'Centros de Costo', group: 'Contabilidad' },
  { key: 'accounting.vouchers', label: 'Comprobantes', group: 'Contabilidad' },
  { key: 'accounting.reconciliation', label: 'Conciliación', group: 'Contabilidad' },
  { key: 'accounting.defaults', label: 'Config. Contable', group: 'Contabilidad' },
  { key: 'accounting.expenses', label: 'Notas de Gasto', group: 'Contabilidad' },
  { key: 'accounting.budgets', label: 'Presupuestos', group: 'Contabilidad' },
  { key: 'accounting.fixed_assets', label: 'Activo Fijo', group: 'Contabilidad' },
  { key: 'accounting.period_lock', label: 'Bloqueo de Período', group: 'Contabilidad' },
  { key: 'accounting.fiscal_year', label: 'Cierre de Ejercicio', group: 'Contabilidad' },
  { key: 'intl_invoices', label: 'Invoices Internacionales', group: 'Contabilidad' },
  { key: 'treasury.entities', label: 'Entidades Factoring', group: 'Tesorería' },
  { key: 'treasury.operations', label: 'Operaciones Factoring', group: 'Tesorería' },
  { key: 'pos.stores', label: 'Locales', group: 'Punto de venta' },
  // Aprobar = generar el código de activación o revocar: entrega o quita la
  // capacidad de emitir a nombre de la empresa.
  { key: 'pos.terminals', label: 'Terminales (aprobar = activar o revocar)', group: 'Punto de venta' },
  { key: 'pos.folios', label: 'Folios de terminales (asignar = entregar un rango · aprobar = revocarlo)', group: 'Punto de venta' },
  { key: 'pos.sales', label: 'Ventas de terminales (monitor de boletas, conflictos y rechazos)', group: 'Punto de venta' },
  { key: 'pos.operators', label: 'Operadores de terminales (cajeros, sus locales y su PIN)', group: 'Punto de venta' },
  { key: 'pos.cash', label: 'Cajas de terminales (aprobar = cuentas del cierre y contabilizarlo)', group: 'Punto de venta' },
  { key: 'inventory.warehouses', label: 'Bodegas', group: 'Inventario' },
  { key: 'inventory.movements', label: 'Movimientos', group: 'Inventario' },
  { key: 'inventory.counts', label: 'Conteos', group: 'Inventario' },
  { key: 'inventory.transfers', label: 'Transferencias', group: 'Inventario' },
  { key: 'receivables', label: 'Cuentas por Cobrar', group: 'Finanzas' },
  { key: 'receivables.aging', label: 'Antigüedad CxC', group: 'Finanzas' },
  { key: 'receivables.collections', label: 'Cobranza CxC', group: 'Finanzas' },
  { key: 'receivables.statements', label: 'Estados Cuenta CxC', group: 'Finanzas' },
  { key: 'receivables.reminders', label: 'Recordatorios CxC', group: 'Finanzas' },
  { key: 'receivables.cash_flow', label: 'Flujo Proyectado CxC', group: 'Finanzas' },
  { key: 'receivables.collection_config', label: 'Config. Cobranza', group: 'Finanzas' },
  { key: 'payables', label: 'Cuentas por Pagar', group: 'Finanzas' },
  { key: 'payables.aging', label: 'Antigüedad CxP', group: 'Finanzas' },
  { key: 'payables.statements', label: 'Estados Cuenta CxP', group: 'Finanzas' },
  { key: 'payables.cash_flow', label: 'Flujo Proyectado CxP', group: 'Finanzas' },
  { key: 'payroll.employees', label: 'Empleados', group: 'Nómina' },
  { key: 'payroll.contracts', label: 'Contratos', group: 'Nómina' },
  { key: 'payroll.periods', label: 'Períodos Nómina', group: 'Nómina' },
  // Gobierna Liquidaciones (solo lectura) y TODA la pantalla de Descuentos y
  // Créditos (crear/editar/eliminar descuentos recurrentes y préstamos). El
  // rótulo lo dice para que se pueda encontrar al asignar permisos.
  { key: 'payroll.payslips', label: 'Liquidaciones y descuentos', group: 'Nómina' },
  { key: 'payroll.vacations', label: 'Vacaciones', group: 'Nómina' },
  { key: 'payroll.terminations', label: 'Finiquitos', group: 'Nómina' },
  { key: 'payroll.bonuses', label: 'Bonos', group: 'Nómina' },
  { key: 'payroll.org_chart', label: 'Organigrama', group: 'Nómina' },
  { key: 'payroll.org_units', label: 'Divisiones y áreas', group: 'Nómina' },
  { key: 'honorarios', label: 'Honorarios (BHE)', group: 'Nómina' },
  { key: 'hr_documents', label: 'Documentos de RRHH', group: 'RRHH' },
  { key: 'hr_documents.signature', label: 'Firma electrónica', group: 'RRHH' },
  { key: 'hr_assets', label: 'Activos asignables', group: 'RRHH' },
  { key: 'time_off', label: 'Solicitudes de tiempo libre', group: 'RRHH' },
  { key: 'reports', label: 'Reportes', group: 'Reportes' },
  { key: 'consolidation', label: 'Consolidacion', group: 'Sistema' },
  { key: 'config.profile', label: 'Perfil de empresa', group: 'Configuración' },
  { key: 'config.localization', label: 'Localización', group: 'Configuración' },
  { key: 'config.currency', label: 'Tipos de cambio', group: 'Configuración' },
  { key: 'config.folios', label: 'Folios', group: 'Configuración' },
  { key: 'config.fields', label: 'Campos personalizados', group: 'Configuración' },
  { key: 'config.templates', label: 'Plantillas PDF', group: 'Configuración' },
  { key: 'config.certificates', label: 'Certificados', group: 'Configuración' },
  { key: 'config.invoicer', label: 'Facturador', group: 'Configuración' },
  { key: 'config.gateways', label: 'Pasarelas de pago', group: 'Configuración' },
  { key: 'config.sso', label: 'SSO', group: 'Configuración' },
  { key: 'config.api_keys', label: 'API Keys', group: 'Configuración' },
  { key: 'config.dte_types', label: 'Documentos DTE', group: 'Configuración' },
  { key: 'config.hr_documents', label: 'Documentos RRHH', group: 'Configuración' },
  { key: 'config.notifications', label: 'Notificaciones', group: 'Configuración' },
  { key: 'config.audit_log', label: 'Bitácora', group: 'Configuración' },
  { key: 'config.historical_import', label: 'Importación histórica', group: 'Configuración' },
]

const ACTIONS = [
  { key: 'view',     label: 'Ver',         cls: 'text-success-content-soft' },
  { key: 'view_own', label: 'Solo propio', cls: 'text-cyan-600 dark:text-cyan-300' },
  { key: 'create',   label: 'Crear',       cls: 'text-primary' },
  { key: 'edit',     label: 'Editar',      cls: 'text-warning-content-soft' },
  { key: 'delete',   label: 'Eliminar',    cls: 'text-danger-content-soft' },
  { key: 'approve',  label: 'Aprobar',     cls: 'text-info-content-soft' },
  { key: 'export',   label: 'Exportar',    cls: 'text-content-muted' },
  { key: 'assign',   label: 'Asignar',     cls: 'text-sky-600 dark:text-sky-300' },
  { key: 'edit_approved', label: 'Editar aprobados', cls: 'text-rose-600 dark:text-rose-300' },
  { key: 'edit_confirmed', label: 'Editar confirmados', cls: 'text-rose-600 dark:text-rose-300' },
]

// Which actions are valid per module
const VALID_ACTIONS: Record<string, string[]> = {
  'users': ['view', 'create', 'edit', 'delete'],
  'config': ['view', 'create', 'edit'],
  'sales.orders': ['view', 'view_own', 'create', 'edit', 'delete', 'approve'],
  'sales.pos': ['create'],
  'sales.documents': ['view', 'view_own', 'create', 'edit', 'delete', 'approve'],
  'sales.customers': ['view', 'create', 'edit', 'delete'],
  'sales.products': ['view', 'create', 'edit', 'delete'],
  'sales.collections': ['view', 'view_own', 'create', 'edit', 'delete', 'approve', 'edit_confirmed'],
  'sales.subscriptions': ['view', 'create', 'edit', 'delete', 'approve'],
  'sales.contracts': ['view', 'view_own', 'create', 'edit', 'delete'],
  'sales.guides': ['view', 'create', 'edit', 'delete'],
  'sales.templates': ['view', 'create', 'edit', 'delete'],
  'sales.commissions': ['view', 'view_own'],
  'sales.forecast': ['view', 'view_own'],
  'purchases.suppliers': ['view', 'create', 'edit', 'delete'],
  'purchases.orders': ['view', 'create', 'edit', 'delete', 'approve'],
  'purchases.invoices': ['view', 'create', 'edit', 'delete', 'approve'],
  'purchases.payments': ['view', 'create', 'edit', 'delete', 'approve', 'edit_confirmed'],
  'dte_inbound': ['view', 'create', 'approve', 'delete'],
  'accounting.accounts': ['view', 'create', 'edit', 'delete'],
  'accounting.cost_centers': ['view', 'create', 'edit', 'delete'],
  'accounting.vouchers': ['view', 'create', 'edit', 'delete', 'approve', 'edit_approved'],
  'accounting.reconciliation': ['view', 'create', 'edit'],
  'accounting.defaults': ['view', 'create', 'edit', 'delete'],
  'accounting.expenses': ['view', 'create', 'edit', 'approve'],
  'accounting.budgets': ['view', 'create', 'edit', 'delete'],
  'accounting.fixed_assets': ['view', 'create', 'edit'],
  'accounting.period_lock': ['view', 'create', 'edit'],
  'accounting.fiscal_year': ['view', 'create', 'edit', 'approve'],
  'intl_invoices': ['view', 'create', 'edit', 'delete', 'approve'],
  'honorarios': ['view', 'create', 'edit', 'delete', 'approve'],
  'treasury.entities': ['view', 'create', 'edit', 'delete'],
  'treasury.operations': ['view', 'create', 'edit', 'delete', 'approve'],
  // Punto de venta
  'pos.stores': ['view', 'create', 'edit', 'delete'],
  'pos.terminals': ['view', 'create', 'edit', 'delete', 'approve'],
  'pos.folios': ['view', 'assign', 'approve'],
  'pos.sales': ['view', 'export', 'approve'],
  'pos.operators': ['view', 'create', 'edit'],
  'pos.cash': ['view', 'approve'],
  // Inventario
  'inventory.warehouses': ['view', 'create', 'edit'],
  'inventory.movements': ['view', 'create', 'edit', 'approve'],
  'inventory.counts': ['view', 'create', 'edit', 'approve'],
  'inventory.transfers': ['view', 'create', 'edit', 'approve'],
  // Finanzas — CxC
  'receivables': ['view', 'view_own', 'create', 'edit'],
  'receivables.aging': ['view', 'view_own'],
  'receivables.collections': ['view', 'create', 'edit'],
  'receivables.statements': ['view', 'view_own'],
  'receivables.reminders': ['view', 'view_own', 'create', 'edit'],
  'receivables.cash_flow': ['view'],
  'receivables.collection_config': ['view', 'create', 'edit', 'delete'],
  // Finanzas — CxP
  'payables': ['view'],
  'payables.aging': ['view'],
  'payables.statements': ['view'],
  'payables.cash_flow': ['view'],
  // Nómina
  'payroll.employees': ['view', 'create', 'edit'],
  'payroll.org_chart': ['view'],
  'payroll.org_units': ['view', 'create', 'edit', 'delete'],
  'payroll.contracts': ['view', 'create', 'edit'],
  'payroll.periods': ['view', 'create', 'edit', 'delete', 'approve'],
  // create/edit/delete NO son de la liquidación (que es solo lectura): son los
  // descuentos recurrentes y los préstamos de /payroll/deductions, que viven bajo
  // este módulo. Sin exponerlas acá no había forma de dárselas a un rol por UI.
  'payroll.payslips': ['view', 'view_own', 'create', 'edit', 'delete', 'approve'],
  'payroll.vacations': ['view', 'create', 'edit', 'delete', 'approve'],
  'payroll.terminations': ['view', 'create', 'edit', 'delete', 'approve'],
  'payroll.bonuses': ['view', 'create', 'edit', 'delete'],
  // RRHH
  'hr_documents': ['view', 'create', 'edit', 'delete'],
  'hr_documents.signature': ['view', 'create', 'edit'],
  'hr_assets': ['view', 'create', 'edit', 'delete', 'assign'],
  'time_off': ['view', 'create', 'edit', 'delete', 'approve'],
  'reports': ['view', 'export'],
  'consolidation': ['view'],
  // Configuración — sub-secciones
  'config.profile': ['view', 'edit'],
  'config.localization': ['view', 'edit'],
  'config.currency': ['view', 'edit'],
  'config.folios': ['view', 'edit'],
  'config.fields': ['view', 'create', 'edit', 'delete'],
  'config.templates': ['view', 'create', 'edit', 'delete'],
  'config.certificates': ['view', 'edit'],
  'config.invoicer': ['view', 'edit'],
  'config.gateways': ['view', 'create', 'edit', 'delete'],
  'config.sso': ['view', 'edit'],
  'config.api_keys': ['view', 'create', 'delete'],
  'config.dte_types': ['view', 'edit'],
  'config.hr_documents': ['view', 'edit'],
  'config.notifications': ['view', 'edit'],
  'config.audit_log': ['view'],
  'config.historical_import': ['view', 'create', 'delete'],
}

// Default permissions per role type (mirrors backend ROLE_PERMISSIONS)
const ROLE_DEFAULTS: Record<string, Record<string, string[]>> = {
  admin: {
    'users': ['view', 'create', 'edit', 'delete'],
    'config': ['view', 'edit'],
    'pos.stores': ['view', 'create', 'edit', 'delete'],
    'pos.terminals': ['view', 'create', 'edit', 'delete', 'approve'],
    'pos.folios': ['view', 'assign', 'approve'],
    'pos.sales': ['view', 'export', 'approve'],
    'pos.operators': ['view', 'create', 'edit'],
    'pos.cash': ['view', 'approve'],
    'sales.orders': ['view', 'create', 'edit', 'delete', 'approve'],
    'sales.documents': ['view', 'create', 'edit', 'delete', 'approve'],
    'sales.customers': ['view', 'create', 'edit', 'delete'],
    'sales.products': ['view', 'create', 'edit', 'delete'],
    'sales.collections': ['view', 'create', 'edit'],
    'sales.subscriptions': ['view', 'create', 'edit', 'delete'],
    'sales.contracts': ['view', 'create', 'edit', 'delete'],
    'sales.guides': ['view', 'create', 'edit', 'delete'],
    'sales.templates': ['view', 'create', 'edit', 'delete'],
    'sales.commissions': ['view'],
    'sales.forecast': ['view'],
    'purchases.suppliers': ['view', 'create', 'edit', 'delete'],
    'purchases.orders': ['view', 'create', 'edit', 'delete', 'approve'],
    'purchases.invoices': ['view', 'create', 'edit', 'delete', 'approve'],
    'purchases.payments': ['view', 'create', 'edit', 'delete'],
    'accounting.accounts': ['view', 'create', 'edit', 'delete'],
    'accounting.cost_centers': ['view', 'create', 'edit', 'delete'],
    'accounting.vouchers': ['view', 'create', 'edit', 'delete', 'approve'],
    'accounting.reconciliation': ['view', 'create', 'edit'],
    'accounting.defaults': ['view', 'edit'],
    'accounting.expenses': ['view', 'create', 'edit', 'approve'],
    'accounting.budgets': ['view', 'create', 'edit', 'delete'],
    'accounting.fixed_assets': ['view', 'create', 'edit'],
    'accounting.period_lock': ['view', 'create', 'edit'],
    'accounting.fiscal_year': ['view', 'create', 'edit', 'approve'],
    'treasury.entities': ['view', 'create', 'edit', 'delete'],
    'treasury.operations': ['view', 'create', 'edit', 'delete', 'approve'],
    'inventory.warehouses': ['view', 'create', 'edit'],
    'inventory.movements': ['view', 'create', 'approve'],
    'inventory.counts': ['view', 'create', 'approve'],
    'inventory.transfers': ['view', 'create', 'approve'],
    'receivables': ['view', 'create', 'edit'],
    'receivables.aging': ['view'],
    'receivables.collections': ['view', 'create', 'edit'],
    'receivables.statements': ['view'],
    'receivables.reminders': ['view', 'create', 'edit'],
    'receivables.cash_flow': ['view'],
    'receivables.collection_config': ['view', 'edit'],
    'payables': ['view'],
    'payables.aging': ['view'],
    'payables.statements': ['view'],
    'payables.cash_flow': ['view'],
    'payroll.employees': ['view', 'create', 'edit'],
    'payroll.contracts': ['view', 'create', 'edit'],
    'payroll.periods': ['view', 'create', 'approve'],
    'payroll.payslips': ['view', 'approve'],
    'payroll.vacations': ['view', 'create', 'approve'],
    'payroll.terminations': ['view', 'create', 'approve'],
    'payroll.bonuses': ['view', 'create', 'edit'],
    'dte_inbound': ['view', 'create', 'approve', 'delete'],
    'intl_invoices': ['view', 'create', 'edit', 'delete', 'approve'],
  'honorarios': ['view', 'create', 'edit', 'delete', 'approve'],
    'reports': ['view', 'export'],
    'consolidation': ['view'],
    'config.historical_import': ['view', 'create', 'delete'],
  },
  accountant: {
    'pos.stores': ['view'],
    'pos.folios': ['view'],
    'pos.sales': ['view', 'export'],
    'sales.orders': ['view', 'create', 'edit', 'approve'],
    'sales.documents': ['view', 'create', 'edit', 'delete', 'approve'],
    'sales.customers': ['view', 'create', 'edit'],
    'sales.products': ['view', 'create', 'edit'],
    'sales.collections': ['view', 'create', 'edit'],
    'sales.subscriptions': ['view', 'create', 'edit'],
    'sales.contracts': ['view', 'create', 'edit'],
    'sales.guides': ['view', 'create', 'edit'],
    'sales.templates': ['view', 'create', 'edit'],
    'sales.commissions': ['view'],
    'sales.forecast': ['view'],
    'purchases.suppliers': ['view', 'create', 'edit'],
    'purchases.orders': ['view', 'create', 'edit', 'approve'],
    'purchases.invoices': ['view', 'create', 'edit', 'delete', 'approve'],
    'purchases.payments': ['view', 'create', 'edit'],
    'accounting.accounts': ['view', 'create', 'edit', 'delete'],
    'accounting.cost_centers': ['view', 'create', 'edit', 'delete'],
    'accounting.vouchers': ['view', 'create', 'edit', 'delete', 'approve'],
    'accounting.reconciliation': ['view', 'create', 'edit'],
    'accounting.defaults': ['view', 'edit'],
    'accounting.expenses': ['view', 'create', 'edit', 'approve'],
    'accounting.budgets': ['view', 'create', 'edit', 'delete'],
    'accounting.fixed_assets': ['view', 'create', 'edit'],
    'accounting.period_lock': ['view', 'create', 'edit'],
    'accounting.fiscal_year': ['view', 'create', 'edit', 'approve'],
    'treasury.entities': ['view'],
    'treasury.operations': ['view'],
    'receivables': ['view', 'create', 'edit'],
    'receivables.aging': ['view'],
    'receivables.collections': ['view', 'create', 'edit'],
    'receivables.statements': ['view'],
    'receivables.reminders': ['view', 'create', 'edit'],
    'receivables.cash_flow': ['view'],
    'receivables.collection_config': ['view', 'edit'],
    'payables': ['view'],
    'payables.aging': ['view'],
    'payables.statements': ['view'],
    'payables.cash_flow': ['view'],
    'dte_inbound': ['view', 'create', 'approve', 'delete'],
    'intl_invoices': ['view', 'create', 'edit', 'delete', 'approve'],
  'honorarios': ['view', 'create', 'edit', 'delete', 'approve'],
    'reports': ['view', 'export'],
  },
  seller: {
    'sales.orders': ['view_own', 'create', 'edit', 'delete'],
    'sales.documents': ['view_own'],
    'sales.customers': ['view'],
    'sales.products': ['view'],
    'sales.collections': ['view_own'],
    'sales.contracts': ['view_own', 'create', 'edit'],
    'sales.guides': ['view', 'create', 'edit'],
    'sales.commissions': ['view_own'],
    'sales.forecast': ['view_own'],
    'inventory.warehouses': ['view'],
    'inventory.movements': ['view_own'],
    'receivables': ['view_own'],
    'receivables.aging': ['view_own'],
    'receivables.statements': ['view_own'],
    'receivables.reminders': ['view_own'],
    'reports': ['view'],
  },
  collector: {
    'sales.documents': ['view'],
    'sales.customers': ['view'],
    'sales.collections': ['view', 'create', 'edit'],
    'receivables': ['view'],
    'receivables.aging': ['view'],
    'receivables.collections': ['view', 'create', 'edit'],
    'receivables.statements': ['view'],
    'receivables.reminders': ['view', 'create', 'edit'],
    'treasury.entities': ['view'],
    'treasury.operations': ['view'],
    'reports': ['view'],
  },
  buyer: {
    'purchases.suppliers': ['view', 'create', 'edit'],
    'purchases.orders': ['view', 'create', 'edit'],
    'purchases.invoices': ['view', 'create', 'edit'],
    'purchases.payments': ['view'],
    'dte_inbound': ['view', 'create', 'approve', 'delete'],
    'reports': ['view'],
  },
  treasurer: {
    'pos.sales': ['view'],
    'sales.documents': ['view'],
    'sales.collections': ['view'],
    'purchases.invoices': ['view'],
    'purchases.payments': ['view'],
    'treasury.entities': ['view', 'create', 'edit', 'delete'],
    'treasury.operations': ['view', 'create', 'edit', 'delete', 'approve'],
    'reports': ['view', 'export'],
  },
  manager: {
    'sales.orders': ['view'],
    'sales.documents': ['view'],
    'sales.customers': ['view'],
    'sales.products': ['view'],
    'sales.collections': ['view'],
    'sales.contracts': ['view'],
    'sales.guides': ['view'],
    'sales.commissions': ['view'],
    'sales.forecast': ['view'],
    'purchases.suppliers': ['view'],
    'purchases.orders': ['view'],
    'purchases.invoices': ['view'],
    'purchases.payments': ['view'],
    'accounting.accounts': ['view'],
    'accounting.cost_centers': ['view'],
    'accounting.vouchers': ['view'],
    'accounting.reconciliation': ['view'],
    'accounting.budgets': ['view'],
    'treasury.entities': ['view'],
    'treasury.operations': ['view'],
    'inventory.warehouses': ['view'],
    'inventory.movements': ['view'],
    'inventory.counts': ['view'],
    'inventory.transfers': ['view'],
    'receivables': ['view'],
    'receivables.aging': ['view'],
    'receivables.collections': ['view'],
    'receivables.statements': ['view'],
    'receivables.reminders': ['view'],
    'receivables.cash_flow': ['view'],
    'payables': ['view'],
    'payables.aging': ['view'],
    'payables.statements': ['view'],
    'payables.cash_flow': ['view'],
    'payroll.employees': ['view'],
    'payroll.contracts': ['view'],
    'payroll.periods': ['view'],
    'payroll.payslips': ['view'],
    'payroll.vacations': ['view'],
    'payroll.terminations': ['view'],
    'payroll.bonuses': ['view'],
    'reports': ['view', 'export'],
    'consolidation': ['view'],
    'pos.stores': ['view'],
    'pos.terminals': ['view'],
    'pos.folios': ['view'],
    'pos.sales': ['view', 'export'],
  },
  viewer: {
    'pos.stores': ['view'],
    'pos.terminals': ['view'],
    'pos.folios': ['view'],
    'pos.sales': ['view'],
    'sales.orders': ['view'],
    'sales.documents': ['view'],
    'sales.customers': ['view'],
    'sales.products': ['view'],
    'sales.collections': ['view'],
    'sales.contracts': ['view'],
    'sales.guides': ['view'],
    'purchases.suppliers': ['view'],
    'purchases.orders': ['view'],
    'purchases.invoices': ['view'],
    'purchases.payments': ['view'],
    'accounting.accounts': ['view'],
    'accounting.cost_centers': ['view'],
    'accounting.vouchers': ['view'],
    'treasury.entities': ['view'],
    'treasury.operations': ['view'],
    'reports': ['view'],
  },
  hr_manager: {
    'payroll.employees': ['view', 'create', 'edit'],
    'payroll.contracts': ['view', 'create', 'edit'],
    'payroll.periods': ['view', 'create', 'approve'],
    'payroll.payslips': ['view', 'approve'],
    'payroll.vacations': ['view', 'create', 'approve'],
    'payroll.terminations': ['view', 'create', 'approve'],
    'payroll.bonuses': ['view', 'create', 'edit'],
    'hr_documents': ['view', 'create', 'edit', 'delete'],
    'hr_documents.signature': ['view', 'create', 'edit'],
    'hr_assets': ['view', 'create', 'edit', 'delete', 'assign'],
    'time_off': ['view', 'create', 'edit', 'delete', 'approve'],
    'sales.customers': ['view'],
    'accounting.vouchers': ['view'],
    'config': ['view'],
    'users': ['view'],
  },
  store_manager: {
    'pos.stores': ['view', 'edit'],
    'pos.terminals': ['view', 'edit'],
    'pos.folios': ['view'],
    'pos.sales': ['view', 'export'],
    'pos.operators': ['view', 'create', 'edit'],
    'pos.cash': ['view'],
    'inventory.warehouses': ['view'],
    'sales.products': ['view'],
  },
  // Solo opera el terminal: lo que hace en la caja va en «En el terminal».
  cashier: {},
}

const ROLE_TYPE_COLORS: Record<string, string> = {
  admin:      'bg-warning-surface text-warning-content border-warning-border',
  accountant: 'bg-success-surface text-success-content border-success-border',
  seller:     'bg-primary-50 text-primary-hover border-primary-200',
  collector:  'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  buyer:      'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  treasurer:  'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800',
  manager:    'bg-surface-muted text-content border-divider',
  viewer:     'bg-surface-muted text-content-muted border-divider',
  hr_manager: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
  store_manager: 'bg-info-surface text-info-content border-info-border',
  cashier:    'bg-info-surface text-info-content border-info-border',
}

// ─── Permission Matrix ────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, { label: string; cls: string }> = Object.fromEntries(
  ACTIONS.map((a) => [a.key, { label: a.label, cls: a.cls }])
)

// Normaliza para búsqueda: lowercase + sin acentos
const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

// Agrupa MODULES por su campo `group`, preservando el orden de aparición.
/** Un recurso de la matriz de permisos. */
type ModuloDePermiso = (typeof MODULES)[number]

function groupModules(modules: typeof MODULES): { name: string; modules: typeof MODULES }[] {
  const groups: { name: string; modules: typeof MODULES }[] = []
  let lastGroup = ''
  for (const m of modules) {
    if (m.group !== lastGroup) {
      groups.push({ name: m.group, modules: [] })
      lastGroup = m.group
    }
    groups[groups.length - 1].modules.push(m)
  }
  return groups
}

function PermissionMatrix({
  permissions,
  onChange,
  readOnly = false,
}: {
  permissions: Record<string, string[]>
  onChange?: (perms: Record<string, string[]>) => void
  readOnly?: boolean
}) {
  const toggle = (module: string, action: string) => {
    if (readOnly || !onChange) return
    const current = permissions[module] || []
    let updated: string[]
    if (current.includes(action)) {
      updated = current.filter((a) => a !== action)
    } else {
      updated = [...current, action]
      // view and view_own are mutually exclusive
      if (action === 'view') updated = updated.filter((a) => a !== 'view_own')
      if (action === 'view_own') updated = updated.filter((a) => a !== 'view')
    }
    onChange({ ...permissions, [module]: updated })
  }

  const hasPerm = (key: string) => (permissions[key]?.length ?? 0) > 0

  // En readOnly mostramos solo los módulos con permisos asignados.
  const baseGroups = useMemo(() => {
    const mods = readOnly ? MODULES.filter((m) => hasPerm(m.key)) : MODULES
    return groupModules(mods)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, permissions])

  // Estado: grupos expandidos. Inicializa expandiendo los que ya tengan permisos
  // (en readOnly todos los grupos visibles van expandidos).
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const g of groupModules(readOnly ? MODULES.filter((m) => hasPerm(m.key)) : MODULES)) {
      init[g.name] = readOnly ? true : g.modules.some((m) => hasPerm(m.key))
    }
    return init
  })
  const [search, setSearch] = useState('')

  const query = normalize(search.trim())

  // Filtrado por búsqueda (label del módulo o nombre del grupo).
  const visibleGroups = useMemo(() => {
    if (!query) return baseGroups
    return baseGroups
      .map((g) => {
        const groupMatches = normalize(g.name).includes(query)
        const mods = groupMatches
          ? g.modules
          : g.modules.filter((m) => normalize(m.label).includes(query))
        return { name: g.name, modules: mods }
      })
      .filter((g) => g.modules.length > 0)
  }, [baseGroups, query])

  // Con búsqueda activa, todos los grupos resultantes se muestran expandidos.
  const isExpanded = (name: string) => (query ? true : !!expanded[name])

  const toggleGroup = (name: string) => {
    if (query) return // sin colapsar mientras se busca
    setExpanded((p) => ({ ...p, [name]: !p[name] }))
  }

  // Encabezado de columnas de acciones para un grupo: solo las acciones válidas
  // entre los módulos del grupo, en el orden canónico de ACTIONS.
  const groupActions = (mods: typeof MODULES) => {
    const set = new Set<string>()
    for (const m of mods) for (const a of VALID_ACTIONS[m.key] ?? []) set.add(a)
    return ACTIONS.filter((a) => set.has(a.key)).map((a) => a.key)
  }

  return (
    <div className="flex flex-col">
      {!readOnly && (
        <div className="relative shrink-0 px-3 py-2.5 border-b border-divider bg-surface">
          <Search size={14} className="absolute left-5 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none" />
          <input aria-label="Buscar permiso"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar permiso…"
            className="w-full pl-8 pr-8 py-2 rounded-lg border border-divider-strong bg-surface text-sm text-content placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-5 top-1/2 -translate-y-1/2 p-0.5 rounded text-content-subtle hover:text-content-muted hover:bg-soft"
              title="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <div className="overflow-y-auto max-h-[420px]">
        {visibleGroups.length === 0 ? (
          <p className="text-center py-10 text-sm text-content-subtle">
            {readOnly ? 'Sin permisos asignados' : 'No se encontraron permisos para esa búsqueda'}
          </p>
        ) : (
          visibleGroups.map((g) => {
            const open = isExpanded(g.name)
            const total = g.modules.length
            const assigned = g.modules.filter((m) => hasPerm(m.key)).length
            const cols = groupActions(g.modules)
            return (
              <div key={g.name} className="border-b border-divider last:border-b-0">
                {/* Cabecera del grupo (acordeón) */}
                <button
                  type="button"
                  onClick={() => toggleGroup(g.name)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 bg-surface-muted hover:bg-soft transition-colors text-left"
                >
                  {open ? (
                    <ChevronDown size={15} className="shrink-0 text-content-muted" />
                  ) : (
                    <ChevronRight size={15} className="shrink-0 text-content-muted" />
                  )}
                  <span className="flex-1 text-sm font-semibold text-content">{g.name}</span>
                  <span className="text-xs tabular-nums text-content-muted">
                    ({assigned}/{total})
                  </span>
                </button>

                {/* Contenido del grupo */}
                {open && (
                  <DataTable<ModuloDePermiso>
                    columns={[
                      { key: 'label', header: 'Recurso',
                        // La sangria alinea con el chevron del acordeon, igual
                        // que antes: es lo que deja leer la jerarquia grupo -> recurso.
                        render: m => (
                          <span className="pl-5 text-sm font-medium whitespace-nowrap">{m.label}</span>
                        ) },
                      ...cols.map(k => ({
                        key: k,
                        width: 52,
                        header: (
                          <span className={ACTION_LABELS[k]?.cls ?? 'text-content-muted'}>
                            {ACTION_LABELS[k]?.label ?? k}
                          </span>
                        ),
                        // Centrada en la celda: el componente solo alinea a la
                        // derecha (`numeric`), y centrar en el `render` es lo que
                        // ya hacen las 28 tablas migradas que lo necesitan.
                        render: (m: ModuloDePermiso) => {
                          const valida = VALID_ACTIONS[m.key]?.includes(k)
                          const marcada = (permissions[m.key] || []).includes(k)
                          return (
                            <div className="text-center">
                              {valida ? (
                                <input
                                  type="checkbox"
                                  checked={marcada}
                                  disabled={readOnly}
                                  aria-label={`${ACTION_LABELS[k]?.label ?? k} en ${m.label}`}
                                  onChange={() => toggle(m.key, k)}
                                  className="w-4 h-4 rounded border-divider-strong text-primary cursor-pointer disabled:cursor-default disabled:opacity-60"
                                />
                              ) : (
                                <span className="text-content-subtle text-base">—</span>
                              )}
                            </div>
                          )
                        },
                      })),
                    ]}
                    rows={g.modules}
                    rowKey={m => m.key}
                    surface={false}
                    rowClassName={() => (readOnly ? undefined : 'hover:bg-primary-50/30')}
                    caption={`Permisos del grupo ${g.name}`}
                  />
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─── Role Modal ───────────────────────────────────────────────────────────────

const ROLE_TYPE_OPTIONS = (Object.keys(ROLE_NAMES) as RoleType[]).map((k) => ({
  value: k,
  label: ROLE_NAMES[k],
}))

function RoleModal({ role, onClose }: { role: Role | null; onClose: () => void }) {
  const isEdit = role !== null
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [roleType, setRoleType] = useState<RoleType>((role?.role_type as RoleType) ?? 'viewer')
  const [permissions, setPermissions] = useState<Record<string, string[]>>(role?.permissions ?? {})
  const [eligibleForCommissions, setEligibleForCommissions] = useState(role?.eligible_for_commissions ?? false)
  const [posCaps, setPosCaps] = useState<string[]>(
    role?.pos_capabilities ?? POS_CAPABILITY_DEFAULTS[role?.role_type ?? 'viewer']?.capabilities ?? [],
  )
  const [posMaxDiscount, setPosMaxDiscount] = useState(
    role?.pos_max_discount_pct ?? POS_CAPABILITY_DEFAULTS[role?.role_type ?? 'viewer']?.maxDiscount ?? '0',
  )
  const [error, setError] = useState<string | null>(null)

  const { mutate: createRole, isPending: isCreating } = useCreateRole()
  const { mutate: updateRole, isPending: isUpdating } = useUpdateRole()
  const isPending = isCreating || isUpdating

  const loadPosDefaults = (type: string) => {
    setPosCaps(POS_CAPABILITY_DEFAULTS[type]?.capabilities ?? [])
    setPosMaxDiscount(POS_CAPABILITY_DEFAULTS[type]?.maxDiscount ?? '0')
  }

  const loadDefaults = () => {
    setPermissions(ROLE_DEFAULTS[roleType] ?? {})
    loadPosDefaults(roleType)
  }

  const handleRoleTypeChange = (type: RoleType) => {
    setRoleType(type)
    if (!isEdit) {
      setPermissions(ROLE_DEFAULTS[type] ?? {})
      setEligibleForCommissions(type === 'seller')
      loadPosDefaults(type)
    }
  }

  const handleSave = () => {
    if (!name.trim()) { setError('El nombre del rol es obligatorio.'); return }
    const tope = Number(posMaxDiscount || 0)
    if (Number.isNaN(tope) || tope < 0 || tope > 100) { setError('El descuento sin autorización va de 0 a 100 %.'); return }
    setError(null)
    const payload = {
      name: name.trim(),
      role_type: roleType,
      description: description.trim(),
      permissions,
      eligible_for_commissions: eligibleForCommissions,
      pos_capabilities: posCaps,
      // Sin descuento por línea, el tope no tiene a qué aplicarse.
      pos_max_discount_pct: posCaps.includes('discount.line') ? String(tope) : '0',
    }

    if (isEdit) {
      updateRole(
        { roleId: role.id, ...payload },
        {
          onSuccess: onClose,
          onError: (e: unknown) => {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            setError(err?.response?.data?.error?.message ?? 'Error al guardar el rol.')
          },
        }
      )
    } else {
      createRole(
        payload,
        {
          onSuccess: onClose,
          onError: (e: unknown) => {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            setError(err?.response?.data?.error?.message ?? 'Error al crear el rol.')
          },
        }
      )
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? `Editar Rol: ${role.name}` : 'Nuevo Rol Personalizado'} subtitle={isEdit ? 'Modifica el nombre y los permisos del rol.' : 'Define un nuevo rol con permisos específicos para la empresa.'} size="2xl" dismissOnScrimClick={false}
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted hover:bg-soft rounded-lg transition-colors">
          Cancelar
        </button>
        <Button onClick={handleSave} disabled={!name.trim()} isPending={isPending}>
          {isEdit ? 'Guardar cambios' : 'Crear rol'}
        </Button>
      </>}
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 space-y-5">
        {error && (
          <div className="p-3 bg-danger-surface border border-danger-border rounded-lg text-danger-content text-sm">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="rolestab-nombre" className="block text-sm font-medium text-content mb-1.5">
              Nombre <span className="text-danger-solid">*</span>
            </label>
            <input id="rolestab-nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Analista Financiero"
              className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="rolestab-tipo-base" className="block text-sm font-medium text-content mb-1.5">Tipo base</label>
            <select id="rolestab-tipo-base"
              value={roleType}
              onChange={(e) => handleRoleTypeChange(e.target.value as RoleType)}
              className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {ROLE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="rolestab-descripcion" className="block text-sm font-medium text-content mb-1.5">Descripción</label>
          <input id="rolestab-descripcion"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Breve descripción del propósito de este rol"
            className="w-full px-3 py-2.5 rounded-lg border border-divider-strong text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex items-center gap-3 p-3 bg-success-surface border border-success-border rounded-lg">
          <input
            type="checkbox"
            id="eligible_for_commissions"
            checked={eligibleForCommissions}
            onChange={(e) => setEligibleForCommissions(e.target.checked)}
            className="h-4 w-4 rounded border-divider-strong text-success-content-soft focus:ring-emerald-500"
          />
          <label htmlFor="eligible_for_commissions" className="text-sm text-content cursor-pointer">
            <span className="font-medium">Elegible para comisiones</span>
            <span className="block text-xs text-content-muted mt-0.5">
              Los usuarios con este rol aparecen como vendedores asignables en pedidos de venta y generan comisiones.
            </span>
          </label>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-content flex items-center gap-1.5">
              Matriz de Permisos
              <span className="text-xs font-normal text-content-subtle">— marca lo que puede hacer este rol</span>
            </h4>
            <button
              type="button"
              onClick={loadDefaults}
              className="text-xs text-primary hover:text-primary-hover hover:underline"
            >
              Cargar valores por defecto
            </button>
          </div>
          <div className="border border-divider rounded-xl overflow-hidden bg-row-alt/50">
            <PermissionMatrix permissions={permissions} onChange={setPermissions} />
          </div>
        </div>

        <PosCapabilitiesSection
          value={posCaps}
          onChange={setPosCaps}
          maxDiscount={posMaxDiscount}
          onMaxDiscountChange={setPosMaxDiscount}
        />
      </div>

      {/* Footer */}
    </Modal>
  )
}

// ─── En el terminal ───────────────────────────────────────────────────────────

/**
 * Las capacidades en el terminal del POS. No son permisos de la web: vender o
 * anular antes de imprimir no es ver ni editar un recurso, por eso van aparte.
 */
function PosCapabilitiesSection({ value, onChange, maxDiscount, onMaxDiscountChange }: {
  value: string[]
  onChange: (v: string[]) => void
  maxDiscount: string
  onMaxDiscountChange: (v: string) => void
}) {
  const toggle = (key: string) =>
    onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key])
  const conDescuento = value.includes('discount.line')

  return (
    <div>
      <h4 className="text-sm font-semibold text-content flex items-center gap-1.5 mb-1">
        En el terminal del POS
        <span className="text-xs font-normal text-content-subtle">— solo aplica a quien opera una caja</span>
      </h4>
      <p className="text-xs text-content-muted mb-3">
        Lo que puede hacer en la caja sin pedir autorización. Lo demás lo aprueba, con su PIN, alguien que tenga «Autorizar en la caja».
      </p>
      <div className="border border-divider rounded-xl p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {POS_CAPABILITIES.map((c) => (
          <label key={c.key} className="flex items-start gap-2 text-sm text-content cursor-pointer">
            <input
              type="checkbox"
              checked={value.includes(c.key)}
              onChange={() => toggle(c.key)}
              className="mt-0.5 h-4 w-4 rounded border-divider-strong"
            />
            <span>
              <span className="font-medium">{c.label}</span>
              <span className="block text-xs text-content-muted">{c.hint}</span>
            </span>
          </label>
        ))}
        <div className="sm:col-span-2 flex items-center gap-2 pt-2 border-t border-divider">
          <label htmlFor="rolestab-pos-descuento" className="text-sm text-content">
            Descuento por línea sin autorización
          </label>
          <input
            id="rolestab-pos-descuento"
            type="number" min={0} max={100} step="0.01"
            value={conDescuento ? maxDiscount : '0'}
            disabled={!conDescuento}
            onChange={(e) => onMaxDiscountChange(e.target.value)}
            className="w-24 px-2 py-1.5 rounded-lg border border-divider-strong text-sm text-content bg-surface disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <span className="text-sm text-content-muted">%</span>
        </div>
      </div>
    </div>
  )
}

// ─── Role Card ────────────────────────────────────────────────────────────────

function RoleCard({
  role,
  onEdit,
  onDelete,
  isDeleting,
}: {
  role: Role
  onEdit: () => void
  onDelete: () => void
  isDeleting: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const isAdminRole = role.role_type === 'admin'
  const colorCls = ROLE_TYPE_COLORS[role.role_type] ?? 'bg-surface-muted text-content-muted border-divider'
  const totalPerms = Object.values(role.permissions ?? {}).reduce(
    (sum, actions) => sum + (Array.isArray(actions) ? actions.length : 0), 0
  )

  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${colorCls}`}>
          {role.role_type_display ?? ROLE_NAMES[role.role_type as RoleType] ?? role.role_type}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-content">{role.name}</span>
            {role.is_system_role && (
              <span className="text-xs text-content-subtle bg-surface-muted px-1.5 py-0.5 rounded font-medium shrink-0">
                Sistema
              </span>
            )}
            {isAdminRole && <Shield size={12} className="text-warning-solid shrink-0" />}
            {role.eligible_for_commissions && (
              <span title="Elegible para comisiones"><BadgeDollarSign size={12} className="text-success-solid shrink-0" /></span>
            )}
          </div>
          {role.description && (
            <p className="text-xs text-content-subtle truncate mt-0.5">{role.description}</p>
          )}
        </div>

        <span className="text-xs text-content-subtle shrink-0 hidden sm:block tabular-nums">
          {totalPerms} perm{totalPerms !== 1 ? 's' : ''}
        </span>

        <button
          onClick={() => setExpanded((p) => !p)}
          className="p-1.5 text-content-subtle hover:text-content-muted hover:bg-soft rounded-lg transition-colors shrink-0"
          title={expanded ? 'Ocultar matriz' : 'Ver permisos'}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        <button
          onClick={onEdit}
          className="p-1.5 text-primary hover:bg-primary-50 rounded-lg transition-colors shrink-0"
          title="Editar permisos"
        >
          <Edit2 size={14} />
        </button>

        <button
          onClick={onDelete}
          disabled={isAdminRole || isDeleting}
          title={isAdminRole ? 'El rol Administrador no puede eliminarse' : 'Eliminar rol'}
          className="p-1.5 text-danger-solid hover:bg-danger-surface rounded-lg transition-colors disabled:text-content-subtle disabled:cursor-not-allowed shrink-0"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-divider pt-3 bg-surface-muted/30">
          <PermissionMatrix permissions={role.permissions ?? {}} readOnly />
        </div>
      )}
    </div>
  )
}

// ─── Roles Tab ────────────────────────────────────────────────────────────────

export function RolesTab() {
  const { data: roles, isLoading } = useRoles()
  const { mutate: deleteRole, isPending: isDeleting } = useDeleteRole()
  const [modalRole, setModalRole] = useState<Role | null | false>(false) // false=closed, null=create, Role=edit
  const { confirm: confirmAction, modalProps } = useConfirmModal()
  const toast = useToast()

  const handleDelete = (role: Role) => {
    if (role.role_type === 'admin') return
    confirmAction({
      message: `¿Eliminar el rol "${role.name}"? Esta acción no se puede deshacer. Los usuarios que tenían este rol deberán ser reasignados.`,
      variant: 'danger',
      onConfirm: () => {
        deleteRole(role.id, {
          onError: (e: unknown) => {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            toast.error(err?.response?.data?.error?.message ?? 'Error al eliminar el rol.')
          },
        })
      },
    })
  }

  return (
    <>
      <ConfirmModal {...modalProps} />
      {modalRole !== false && (
        <RoleModal role={modalRole} onClose={() => setModalRole(false)} />
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-content">Roles y Permisos</h2>
          <p className="text-sm text-content-subtle mt-0.5">
            Gestiona los roles de tu empresa y sus permisos por módulo
          </p>
        </div>
        <Button onClick={() => setModalRole(null)} leftIcon={<Plus size={15} />}>
          Nuevo Rol
        </Button>
      </div>

      {/* Info note */}
      <div className="flex items-start gap-2 p-3 bg-primary-50 border border-primary-100 rounded-xl text-xs text-primary-hover mb-5">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>
          Los roles marcados como <strong>Sistema</strong> son predefinidos y pueden editarse para ajustarlos a tu empresa.
          El rol <strong>Administrador</strong> no puede eliminarse ya que es necesario para gestionar el acceso al sistema.
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />
          Cargando roles...
        </div>
      ) : (
        <div className="space-y-2">
          {(roles ?? []).map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              onEdit={() => setModalRole(role)}
              onDelete={() => handleDelete(role)}
              isDeleting={isDeleting}
            />
          ))}
          {(roles ?? []).length === 0 && (
            <EmptyState title="No hay roles configurados" size="sm" />
          )}
        </div>
      )}
    </>
  )
}
